import { useEffect, useState } from "react";
import config from "../../constants/config.js";

function MigrationDetailsView({
    migration,
    shops,
    onClose
}) {
    const availableTabs = [
        "errors",
        "products",
    ];
    const intervalTimeMS = 2000;

    const [ migrationLocal , setMigration ] = useState( migration );
    const [ activeTab, setActiveTab ] = useState( availableTabs[1] );

    let shouldTrack = true;
    if ( migration?.status === "FINISHED" || migration?.status === "ABORTED" ) {
        shouldTrack = false;
    }
    const [ shouldTrackProgress , setShouldTrackProgress ] = useState( shouldTrack );

    useEffect(() => {
        if ( !migrationLocal || !shouldTrackProgress ) {
            return;
        }

        const interval = setInterval(() => {
            getMigrationById( migrationLocal?.id ).then( ( data ) => {
                setMigration( data );
            });

        }, intervalTimeMS );

        return () => clearInterval( interval );
    }, [ migrationLocal , shouldTrackProgress ]);

    function getSafeProgress() {
        return Math.min(100, Math.max( 0, migrationLocal?.progress ));
    }

    function getColor( p ) {
        const hue = (p / 100) * 120;
        const background = `hsl(${hue}, 80%, 50%)`;
        return background;
    }

    async function getMigrationById( id ) {
        let migrationData = null;

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/migrations/${encodeURIComponent( id )}`;
            const requestSettings = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            
            const response = await fetch( url , requestSettings );
            const data = await response.json();

            if ( data != null ) {
                migrationData = data;
            }
        } catch ( error ) {
            console.log( error );
        }

        return migrationData;
    }

    function formatStatus( status ) {
        let formattedStatus = status;

        const migrationStatus = {
            "STARTED": {
                background_color: "#f7cfc5ff",
                text_color: "#8a2d16ff",
                display_value: "MIGRATION STARTED"
            },
            "GATHERING_PRODUCT_DATA": {
                background_color: "#D1C4E9", 
                text_color: "#6137dfff",
                display_value: "GATHERING PRODUCT DATA"
            },
            "SORTING_PRODUCT_DATA": {
                background_color: "#F3E5F5",
                text_color: "#370f67ff",
                display_value: "ORGANIZING PRODUCT DATA"
            },
            "DELETING_PRODUCTS": {
                background_color: "#FFEBEE",
                text_color: "#d55785ff",
                display_value: "REMOVING OLD PRODUCTS"
            },
            "CREATING_PRODUCTS": {
                background_color: "#E3F2FD", 
                text_color: "#0D47A1",
                display_value: "CREATING NEW PRODUCTS"
            },
            "UPDATING_PRODUCT_METADATA": {
                background_color: "#F3E5F5",
                text_color: "#4A148C",
                display_value: "UPDATING PRODUCT DETAILS"
            },
            "FINISHED": {
                background_color: "#E8F5E9",
                text_color: "#2E7D32",
                display_value: "MIGRATION COMPLETE"
            },
            "ABORTED": {
                background_color: "#FFEBEE",
                text_color: "#C62828",
                display_value: "MIGRATION ABORTED"
            }
        };

        const badgeInfo = migrationStatus[ status ];
        formattedStatus = (
            <div className="status-badges">
                <div
                    style={{ 
                        backgroundColor: badgeInfo?.background_color || '#ffffff',
                        color: badgeInfo?.text_color || '#000000' 
                    }} 
                    className="status-badge"
                >
                    { badgeInfo?.display_value || "" }
                </div>
            </div>
        );


        return formattedStatus;   
    }

    return(
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content extra-wide" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>
                    <span>×</span>
                    <span className="sr-only">Close modal</span>
                </button>

                <div className="stats">
                    <div className="left">
                        <div className="heading">
                            <h2>Migration Statistics - {migrationLocal?.id}</h2>
                        </div>

                        {/* status */}
                        <div className="product-count">
                            <p><strong>Status</strong></p>
                            { formatStatus( migrationLocal?.status ) }
                        </div>

                        <div className="from-store">
                            <p><strong>From Store:</strong></p>
                            {migrationLocal?.from_store}
                        </div>

                        {/* To Store */}
                        <div className="from-store">
                            <p><strong>To Store:</strong></p>
                            <p>{migrationLocal?.to_store}</p>
                        </div>

                        {/* Overwrite Products? */}
                        <div className="overwrite-products">
                            <p><strong>Overwrite Products:</strong></p>
                            <p>{migrationLocal?.overwrite_products ? "YES" : "NO"}</p>
                        </div>

                        {/* Total Products */}
                        <div className="total-count">
                            <p><strong>Total Products:</strong></p>
                            <p>{migrationLocal?.product_count ?? 0}</p>
                        </div>

                        {/* Created By */}
                        <div className="created-by">
                            <p><strong>Created By:</strong></p>
                            <p>{migrationLocal?.created_by}</p>
                        </div>

                        {/* Created At */}
                        <div className="created-timestamp">
                            <p><strong>Created Timestamp:</strong></p>
                            <p>{new Date( migrationLocal?.created_at ).toLocaleString()}</p>
                        </div>

                        {/* Last Updated At */}
                        <div className="last-updated">
                            <p><strong>Last Updated Timestamp:</strong></p>
                            <p>{new Date( migrationLocal?.updated_at ).toLocaleString()}</p>
                        </div>
                    </div>

                    <div className="right">
                        <div className="tab-container">
                            <div className="tab-header">
                                <button
                                    className={`tab-button ${activeTab === availableTabs[1] ? "active" : ""}`}
                                    onClick={() => setActiveTab( availableTabs[1] )}
                                >
                                    Products Sent
                                </button>

                                <button
                                    className={`tab-button ${activeTab === availableTabs[0] ? "active" : ""}`}
                                    onClick={() => setActiveTab( availableTabs[0] )}
                                >
                                    Errors Reported
                                </button>
                            </div>

                            <div className="tab-body">
                                {activeTab === availableTabs[0] && (
                                    <div className="code-wrapper">
                                        <pre className="code-block">
                                            { JSON.stringify( migrationLocal?.reported_errors , null , 2 ) }
                                        </pre>
                                    </div>
                                )}

                                {activeTab === availableTabs[1] && (
                                    <div className="code-wrapper">
                                        <pre className="code-block">
                                            { JSON.stringify( migrationLocal?.products , null , 2 ) }
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <h2>Track Progress</h2>
                <div className="progress">
                    <div
                        className="progress-bar"
                        style={{ 
                            width: `${getSafeProgress()}%`,
                            background: getColor(getSafeProgress()),
                        }}
                    >
                        <span className="progress-text">{getSafeProgress()}%</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MigrationDetailsView;